import type { EntityStore } from '@datorama/akita';
import { EntityState, getEntityType, getIDType, QueryEntity } from '@datorama/akita';
import { objectify } from '@y42/util/objectify';
import { defer, map, Observable, shareReplay } from 'rxjs';
import { AbstractGitEntityState } from '../../models/abstract-git-entity-state.model';
import { AbstractStoreModel } from '../../models/abstract-store-model.model';

export interface AbstractGitQueryEntityConfig<
  State extends AbstractGitEntityState = AbstractGitEntityState,
  UIModel extends AbstractStoreModel = AbstractStoreModel,
> {
  store: EntityStore<State>;
  /** A list of observables that should cause the active$ and items$ observables to recalculate. */
  // triggers$?: Array<Observable<unknown>>;
  /** A function that maps the StoreModel to the UIModel. */
  toUIModel: UIModelCallback<State, UIModel>;
}

export type UIModelCallback<State extends AbstractGitEntityState | EntityState, UIModel extends AbstractStoreModel> = (
  storeModel: getEntityType<State>,
) => UIModel;

export abstract class AbstractGitQueryEntity<
  State extends AbstractGitEntityState = AbstractGitEntityState,
  UIModel extends AbstractStoreModel = AbstractStoreModel,
> extends QueryEntity<State> {
  constructor(protected readonly cfg: AbstractGitQueryEntityConfig<State, UIModel>) {
    super(cfg.store);
  }

  readonly toUIModel = this.cfg.toUIModel;

  readonly itemsByPath$: Observable<Record<UIModel['$path'], UIModel>> = defer(() => this.selectAll()).pipe(
    map((items) => items.map((item) => this.toUIModel(item))),
    map((items) => objectify(items, '$path')),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  selectUIEntity(id: getIDType<State>): Observable<UIModel | undefined> {
    return this.selectEntity(id).pipe(map((entity) => (entity ? this.toUIModel(entity) : undefined)));
  }

  getUIEntity(id: getIDType<State>): UIModel | undefined {
    const entity = this.getEntity(id);
    return entity ? this.toUIModel(entity) : undefined;
  }

  getActiveUIModel(): UIModel | undefined {
    const active = this.getActive();
    return active ? this.toUIModel(active as getEntityType<State>) : undefined;
  }

  getAllUIModels(): UIModel[] {
    return this.getAll().map((item) => this.toUIModel(item));
  }
}
